import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { ChartScatter, Eye, EyeOff, Grid, MousePointerClick, RotateCcw, Settings, Sliders, SlidersHorizontal, X } from 'lucide-react';
import * as THREE from 'three';
import { usePatientZScores } from '../../NormativeModeling';
import { getBrainBrowserShapes } from '../lib/atlasColoring';
import { CLINICAL_OVERLAY_LEGEND } from '../lib/patientReport';
import { isPatientOrClinicalPointName } from '../lib/pointNames';
import { addAtlasRegionLabelSprites, cleanAtlasRegionName, clearAtlasRegionLabelSprites, selectAtlasRegionLabels } from '../lib/regionLabels';
import { SettingsPanel } from './SettingsPanel';


// -------------------------------
// Subcomponent: BrainVisualizer
// Main 3D visualization canvas and view controls
// -------------------------------
export const BrainVisualizer = forwardRef((props, ref) => {
    const {
        viewer,
        hasIntensityData,
        setHasIntensityData,
        selectedCoordinates,
        setSelectedCoordinates,
        selectedVertex,
        setSelectedVertex,
        selectedValue,
        setSelectedValue,
        selectedColor,
        setSelectedColor,
        brainOpacity,
        setBrainOpacity,
        wireframeEnabled,
        setWireframeEnabled,
        onResetSettings,
        resetNonce,
        currentObjectFilename = '',
        selectedFormat,
        setSelectedFormat,
        selectedBackground,
        setSelectedBackground,
        setCurrentObjectFilename,
        settingsState,
        setSettingsState,
        patientsDataLoading
    } = props;

    // ... internal state (containerRef, isBrainVisible, etc.) ...
    const containerRef = useRef(null);
    const [isBrainVisible, setIsBrainVisible] = useState(true);
    const [arePointsVisible, setArePointsVisible] = useState(true);
    const [hasPoints, setHasPoints] = useState(false);
    const markerRef = useRef(null);
    // Add state for selected brain region
    const [selectedRegion, setSelectedRegion] = useState(null);
    // Add state for settings panel visibility
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    // Add state for coordinate panel collapse
    const [isCoordinatePanelCollapsed, setIsCoordinatePanelCollapsed] = useState(true);
    // Add state for view controls collapse
    const [isViewControlsCollapsed, setIsViewControlsCollapsed] = useState(true);
    const [showRegionLabels, setShowRegionLabels] = useState(false);
    const [regionLabelPositions, setRegionLabelPositions] = useState([]);

    // Clinical metadata legend floats over the 3D view. Read it straight from the
    // z-score context so the swatches mirror the overlay markers drawn on the
    // brain; only show it once z-scores are plotted with the overlay enabled.
    const { zScoreVisualizationSettings, patientZScores } = usePatientZScores();
    const showClinicalLegend = Boolean(zScoreVisualizationSettings?.showClinicalOverlay)
        && patientZScores.length > 0;
    const [isLegendCollapsed, setIsLegendCollapsed] = useState(false);

    // Refs for atlas data
    const atlasLabelsRef = useRef(null);
    const atlasDataRef = useRef(null);
    const currentAtlasRef = useRef(null);
    const regionCentroidsRef = useRef([]);
    const drawCameraRef = useRef(null);

    // Create a ref to expose arePointsVisible to other components
    const pointsVisibilityRef = useRef(true);

    // Determine which atlas file to load based on the object filename
    const getAtlasFile = useCallback(() => {
        const filename = currentObjectFilename.toLowerCase();

        // Logic to select atlas based on uploaded object filename
        // TODO: This is a temporary solution
        if (filename.includes('lh') && filename.includes('rh')) {
            return '/atlas/atlas.txt';
        } else if (filename.includes('rh') || filename.includes('right')) {
            return '/atlas/atlas_rh.txt';
        } else if (filename.includes('lh') || filename.includes('left')) {
            return '/atlas/atlas_lh.txt';
        } else {
            return '/atlas/atlas.txt';
        }
    }, [currentObjectFilename]);

    // Helper function to update region from vertex - Must be defined BEFORE the useEffect that references it
    const updateRegionFromVertex = useCallback((vertexIndex) => {
        if (atlasDataRef.current && atlasLabelsRef.current && vertexIndex !== undefined) {
            // Get region number from vertex index
            const regionNumber = atlasDataRef.current[vertexIndex];
            if (regionNumber !== undefined) {
                // Get region name from region number
                const regionName = atlasLabelsRef.current[regionNumber];
                setSelectedRegion(regionName || "Unknown");
            } else {
                setSelectedRegion("Region not found");
            }
        } else {
            setSelectedRegion("Atlas data not loaded");
        }
    }, []);

    const buildRegionCentroids = useCallback(() => {
        const atlasData = atlasDataRef.current;
        const atlasLabels = atlasLabelsRef.current;
        const shapes = getBrainBrowserShapes(viewer);
        const BrainBrowserTHREE = window.BrainBrowser?.SurfaceViewer?.THREE || THREE;

        if (!atlasData || !atlasLabels || shapes.length === 0) {
            regionCentroidsRef.current = [];
            setRegionLabelPositions([]);
            return;
        }

        viewer?.model?.updateMatrixWorld?.(true);
        const regionStats = new Map();

        shapes.forEach((shape) => {
            const indices = shape.userData?.original_data?.indices || [];
            const positions = shape.geometry?.attributes?.position?.array;
            if (!indices.length || !positions) return;

            const stride = Math.max(1, Math.ceil(indices.length / 45000));
            for (let i = 0; i < indices.length; i += stride) {
                const atlasVertexIndex = Number(indices[i]);
                const labelId = Number(atlasData[atlasVertexIndex]);
                if (!Number.isFinite(labelId) || labelId <= 0) continue;

                const regionName = atlasLabels[labelId];
                if (!regionName) continue;

                let localIndex = atlasVertexIndex;
                if (localIndex * 3 + 2 >= positions.length) {
                    localIndex = i;
                }
                const coordinateIndex = localIndex * 3;
                if (coordinateIndex + 2 >= positions.length) continue;

                const localPosition = new BrainBrowserTHREE.Vector3(
                    positions[coordinateIndex],
                    positions[coordinateIndex + 1],
                    positions[coordinateIndex + 2]
                );
                const worldPosition = shape.localToWorld(localPosition);
                const modelPosition = viewer?.model?.worldToLocal
                    ? viewer.model.worldToLocal(worldPosition.clone())
                    : worldPosition;

                const current = regionStats.get(labelId) || {
                    id: labelId,
                    name: cleanAtlasRegionName(regionName),
                    rawName: regionName,
                    count: 0,
                    x: 0,
                    y: 0,
                    z: 0
                };
                current.count += 1;
                current.x += modelPosition.x;
                current.y += modelPosition.y;
                current.z += modelPosition.z;
                regionStats.set(labelId, current);
            }
        });

        regionCentroidsRef.current = Array.from(regionStats.values())
            .filter(region => region.count >= 3)
            .map(region => {
                const position = new BrainBrowserTHREE.Vector3(
                    region.x / region.count,
                    region.y / region.count,
                    region.z / region.count
                );
                const length = Math.sqrt(
                    (position.x * position.x) +
                    (position.y * position.y) +
                    (position.z * position.z)
                ) || 1;
                position.x += (position.x / length) * 5;
                position.y += (position.y / length) * 5;
                position.z += (position.z / length) * 5;
                return {
                    ...region,
                    position
                };
            })
            .sort((a, b) => b.count - a.count);
    }, [viewer]);

    const updateRegionLabelPositions = useCallback((camera) => {
        const activeCamera = camera || drawCameraRef.current;
        const container = containerRef.current;
        if (!showRegionLabels || !viewer?.model || !activeCamera || !container) {
            setRegionLabelPositions([]);
            return;
        }

        if (regionCentroidsRef.current.length === 0) {
            buildRegionCentroids();
        }

        const selectedRegions = selectAtlasRegionLabels(regionCentroidsRef.current);
        const width = container.clientWidth || 1;
        const height = container.clientHeight || 1;
        const occupied = [];
        const nextLabels = [];
        const BrainBrowserTHREE = window.BrainBrowser?.SurfaceViewer?.THREE || THREE;

        activeCamera.updateMatrixWorld?.(true);
        activeCamera.updateProjectionMatrix?.();
        viewer.model.updateMatrixWorld?.(true);

        selectedRegions.forEach((region) => {
            const worldPosition = region.position.clone
                ? region.position.clone()
                : new BrainBrowserTHREE.Vector3(region.position.x, region.position.y, region.position.z);

            if (viewer.model.localToWorld) {
                viewer.model.localToWorld(worldPosition);
            }

            const projected = worldPosition.project(activeCamera);
            if (!Number.isFinite(projected.x) || !Number.isFinite(projected.y) || projected.z < -1 || projected.z > 1) {
                return;
            }

            const x = ((projected.x + 1) / 2) * width;
            const y = ((-projected.y + 1) / 2) * height;
            if (x < 16 || x > width - 16 || y < 16 || y > height - 16) {
                return;
            }

            const tooClose = occupied.some(position => (
                Math.abs(position.x - x) < 92 && Math.abs(position.y - y) < 34
            ));
            if (tooClose) return;

            occupied.push({ x, y });
            nextLabels.push({
                id: region.id,
                name: region.name,
                x,
                y,
                z: projected.z
            });
        });

        setRegionLabelPositions(nextLabels);
    }, [showRegionLabels, viewer, buildRegionCentroids]);

    const renderRegionLabelSprites = useCallback((visible = showRegionLabels) => {
        if (!viewer?.model) return;
        clearAtlasRegionLabelSprites(viewer);
        if (!visible) {
            viewer.render?.();
            return;
        }
        if (regionCentroidsRef.current.length === 0) {
            buildRegionCentroids();
        }
        addAtlasRegionLabelSprites(viewer, selectAtlasRegionLabels(regionCentroidsRef.current));
        viewer.render?.();
    }, [showRegionLabels, viewer, buildRegionCentroids]);

    // Load atlas data when component mounts or when object file changes
    useEffect(() => {
        // Function to load and parse atlas-labels.txt
        const loadAtlasLabels = async () => {
            try {
                const response = await fetch('/atlas/atlas-labels.txt');
                const text = await response.text();
                const lines = text.split('\n').filter(line => line.trim() !== '');

                const labelMap = {};
                lines.forEach(line => {
                    const parts = line.split('\t');
                    if (parts.length === 2) {
                        const regionName = parts[0].replace(/'/g, '').trim();
                        const regionNumber = parseInt(parts[1], 10);
                        labelMap[regionNumber] = regionName;
                    }
                });

                atlasLabelsRef.current = labelMap;
                regionCentroidsRef.current = [];
                if (showRegionLabels) {
                    window.setTimeout(() => renderRegionLabelSprites(true), 50);
                    window.setTimeout(() => updateRegionLabelPositions(), 80);
                }
                console.log('Atlas labels loaded');
            } catch (error) {
                console.error('Error loading atlas labels:', error);
            }
        };

        // Function to load the appropriate atlas data file
        const loadAtlasData = async () => {
            try {
                const atlasFile = getAtlasFile();
                if (atlasFile === currentAtlasRef.current) {
                    return; // Skip if we're already using this atlas file
                }

                console.log(`Loading atlas file: ${atlasFile}`);
                currentAtlasRef.current = atlasFile;

                const response = await fetch(atlasFile);
                const text = await response.text();
                const lines = text.split('\n').filter(line => line.trim() !== '');

                const vertexMap = {};
                lines.forEach((line, index) => {
                    const regionNumber = parseInt(line.trim(), 10);
                    if (!isNaN(regionNumber)) {
                        vertexMap[index] = regionNumber;
                    }
                });

                atlasDataRef.current = vertexMap;
                regionCentroidsRef.current = [];
                if (showRegionLabels) {
                    window.setTimeout(() => renderRegionLabelSprites(true), 50);
                    window.setTimeout(() => updateRegionLabelPositions(), 80);
                }
                console.log('Atlas data loaded');
            } catch (error) {
                console.error('Error loading atlas data:', error);
            }
        };

        // Load both atlas files only when component mounts or when the atlas file changes
        loadAtlasLabels();
        loadAtlasData();
    }, [getAtlasFile, showRegionLabels, renderRegionLabelSprites, updateRegionLabelPositions]); // Remove selectedVertex from dependencies

    // Add a separate effect to update region when selected vertex changes
    useEffect(() => {
        if (selectedVertex !== null && atlasDataRef.current && atlasLabelsRef.current) {
            updateRegionFromVertex(selectedVertex);
        }
    }, [selectedVertex, updateRegionFromVertex]);

    useEffect(() => {
        if (!showRegionLabels) {
            clearAtlasRegionLabelSprites(viewer);
            setRegionLabelPositions([]);
            viewer?.render?.();
            return;
        }
        regionCentroidsRef.current = [];
        window.setTimeout(() => renderRegionLabelSprites(true), 0);
        window.setTimeout(() => updateRegionLabelPositions(), 50);
    }, [showRegionLabels, currentObjectFilename, viewer, renderRegionLabelSprites, updateRegionLabelPositions]);

    useEffect(() => {
        if (!viewer?.addEventListener) return undefined;

        const handleDraw = (event) => {
            if (event?.camera) {
                drawCameraRef.current = event.camera;
                if (showRegionLabels) {
                    window.requestAnimationFrame(() => updateRegionLabelPositions(event.camera));
                }
            }
        };

        viewer.addEventListener('draw', handleDraw);
        return undefined;
    }, [viewer, showRegionLabels, updateRegionLabelPositions]);

    // useEffects for points visibility, intensity data check, pointsAdded listener
    useEffect(() => {
        pointsVisibilityRef.current = arePointsVisible;
        // Set a data attribute on the document body to make it globally accessible
        document.body.setAttribute('data-points-visible', arePointsVisible);
    }, [arePointsVisible]);

    useEffect(() => {
        if (viewer) {
            const handleIntensityDataLoad = () => setHasIntensityData(true);

            // Add event listener for intensity data loading
            if (viewer.addEventListener) {
                viewer.addEventListener("loadintensitydata", handleIntensityDataLoad);
            }

            // Check if intensity data is available 
            const checkIntensityData = () => {
                if (viewer.model_data) {
                    const hasData = viewer.model_data.intensity_data &&
                        viewer.model_data.intensity_data.length > 0 &&
                        viewer.model_data.intensity_data[0].values;
                    setHasIntensityData(hasData);
                }
            };

            checkIntensityData();
            const timeoutId = setTimeout(checkIntensityData, 1000);

            // Cleanup function
            return () => {
                if (viewer.removeEventListener) {
                    viewer.removeEventListener("loadintensitydata", handleIntensityDataLoad);
                }
                clearTimeout(timeoutId);
            };
        }
    }, [viewer, setHasIntensityData]);

    // Sync local hasIntensityData with parent state
    useEffect(() => {
        setHasIntensityData(hasIntensityData);
    }, [hasIntensityData, setHasIntensityData]);

    // Check for points whenever the viewer updates
    useEffect(() => {
        if (viewer) {
            const checkForPoints = () => {
                // Add safety check for viewer.model
                if (!viewer.model) {
                    console.warn("Viewer model not available");
                    setHasPoints(false);
                    return;
                }

                // Check for both patient points AND uploaded points using traverse for deeper search
                let foundPoints = false;

                viewer.model.traverse(object => {
                    if (object && object.name) {
                        if (isPatientOrClinicalPointName(object.name) || object.name === 'uploaded-points') {
                            foundPoints = true;
                        }
                    }
                });

                setHasPoints(foundPoints);
            };

            try { checkForPoints(); } catch (error) { console.error("Error checking for points:", error); }

            // Create an observer to check whenever the model changes
            const originalRender = viewer.render;
            viewer.render = function () {
                originalRender.call(this);
                checkForPoints();
            };

            return () => {
                if (viewer) viewer.render = originalRender; // Restore original render function
            };
        }
    }, [viewer]);

    // Add this new effect at the beginning of the BrainVisualizer component,
    // after all the state declarations and before any other useEffect
    useEffect(() => {
        const handlePointsAdded = () => setHasPoints(true);
        document.addEventListener('pointsAdded', handlePointsAdded);
        return () => document.removeEventListener('pointsAdded', handlePointsAdded);
    }, []);


    // --- Handlers ---
    // Ensure handlers use destructured props directly

    const handleTogglePoints = () => {
        if (viewer) {
            // Toggle the state first
            const newVisibility = !arePointsVisible;
            setArePointsVisible(newVisibility);

            // Set the body attribute for global access
            document.body.setAttribute('data-points-visible', newVisibility);

            // Apply to all objects using traverse for deep search
            viewer.model.traverse(object => {
                // Check if this is a valid object with a name property
                if (object && object.visible !== undefined && object.name) {
                    if (isPatientOrClinicalPointName(object.name)) object.visible = newVisibility;
                    if (object.name === 'uploaded-points') object.visible = newVisibility;
                }
            });

            // Ensure the scene is re-rendered
            viewer.render();
        }
    };

    const handleResetView = () => {
        if (viewer) {
            viewer.resetView();
            applyOpacityToScene(brainOpacity);
            applyWireframeState(wireframeEnabled);
            viewer.render();

            // Dispatch custom event to notify ControlPanel
            document.dispatchEvent(new CustomEvent('resetView'));
        }
    };

    const handleToggleBrain = () => {
        if (viewer) {
            // Find all brain models (any mesh that isn't a sphere/point)
            const brainModels = [];
            viewer.model.traverse(object => {
                if (object.type === 'Mesh' && object.geometry && object.geometry.type !== 'SphereGeometry' &&
                    !object.name?.includes('-points') && object.name !== 'uploaded-points') {
                    brainModels.push(object);
                }
            });

            if (brainModels.length > 0) {
                // Toggle visibility for all brain models
                const newVisibility = !isBrainVisible;
                brainModels.forEach(model => model.visible = newVisibility);
                setIsBrainVisible(newVisibility);
                viewer.render();
            }
        }
    };

    const handleClearSelection = () => {
        // Clear the selected coordinates and vertex
        setSelectedCoordinates(null);
        setSelectedVertex(null);
        setSelectedValue(null);
        setSelectedColor(null);
        setSelectedRegion(null);

        // Remove marker if any
        if (markerRef.current && viewer) {
            try {
                // Clear any pulse intervals
                if (markerRef.current.pulseInterval) {
                    clearInterval(markerRef.current.pulseInterval);
                    markerRef.current.pulseInterval = null;
                }
                if (viewer.model && typeof viewer.model.remove === 'function') viewer.model.remove(markerRef.current);
                else if (viewer.scene && typeof viewer.scene.remove === 'function') viewer.scene.remove(markerRef.current);
                if (typeof viewer.clearDots === 'function') viewer.clearDots();
            } catch (error) { console.warn("Error removing marker:", error); }
            markerRef.current = null;
            viewer.render();
        }
    };

    // UseEffect for viewport updates (already correct)
    const updateViewerViewport = useCallback(() => {
        if (viewer && typeof viewer.updateViewport === 'function') {
            viewer.updateViewport();
            viewer.render();
        } else if (viewer && viewer.renderer && viewer.camera) {
            const container = containerRef.current;
            if (container) {
                const width = container.clientWidth; const height = container.clientHeight;
                viewer.renderer.setSize(width, height);
                viewer.camera.aspect = width / height;
                viewer.camera.updateProjectionMatrix();
                viewer.render();
            }
        }
    }, [viewer]);

    useImperativeHandle(ref, () => ({
        triggerViewportUpdate: updateViewerViewport,
        captureImage: () => {
            try {
                viewer?.render?.();
                if (typeof viewer?.canvasDataURL === 'function') {
                    return viewer.canvasDataURL();
                }
                const canvas = viewer?.renderer?.domElement || containerRef.current?.querySelector('canvas');
                return canvas?.toDataURL?.('image/png') || null;
            } catch (error) {
                console.warn('Unable to capture brain viewer image:', error);
                return null;
            }
        }
    }));

    useEffect(() => {
        const resizeObserver = new ResizeObserver(() => updateViewerViewport());
        const currentContainer = containerRef.current;
        if (currentContainer) resizeObserver.observe(currentContainer);
        updateViewerViewport(); // Initial call
        return () => {
            if (currentContainer) resizeObserver.unobserve(currentContainer);
            resizeObserver.disconnect();
        };
    }, [updateViewerViewport]);

    // UseEffect for click handling - update to use the consolidated atlas data reference
    useEffect(() => {
        // Add click event handler for coordinate picking
        if (viewer) {
            const handleClick = (event) => {
                // Only process if shift key is pressed
                if (!event.shiftKey) return;
                const container = containerRef.current; if (!container) return;
                if (!viewer || !viewer.model || !viewer.model.children) return;

                const rect = container.getBoundingClientRect();
                const x = event.clientX - rect.left; const y = event.clientY - rect.top;

                // Use BrainBrowser's built-in picking functionality
                if (typeof viewer.pick === 'function') {
                    try {
                        const pick = viewer.pick(x, y);

                        if (pick && pick.point) {
                            const point = pick.point;
                            const index = pick.index;

                            // Try to get the actual vertex position if possible
                            let vertexPosition = point;

                            // If we have a valid index and can access the model's geometry
                            if (index !== undefined && viewer.model) {
                                try {
                                    // Find the brain model
                                    const brainModel = viewer.model.children.find(child =>
                                        child.type === 'Mesh' && child.geometry?.type !== 'SphereGeometry'
                                    );

                                    if (brainModel && brainModel.geometry) {
                                        const geometry = brainModel.geometry;
                                        if (geometry.attributes && geometry.attributes.position) {
                                            const positionAttribute = geometry.attributes.position;
                                            if (positionAttribute.array && index * 3 + 2 < positionAttribute.array.length) {
                                                // Directly use array indices for more reliable vertex position extraction
                                                const x = positionAttribute.array[index * 3];
                                                const y = positionAttribute.array[index * 3 + 1];
                                                const z = positionAttribute.array[index * 3 + 2];
                                                vertexPosition = new THREE.Vector3(x, y, z);
                                            }
                                        } else if (geometry.vertices && geometry.vertices[index]) {
                                            vertexPosition = geometry.vertices[index];
                                        }
                                    }
                                } catch (geomErr) {
                                    console.warn("Could not get precise vertex position, using pick point.", geomErr);
                                }
                            }

                            // Update state with coordinates and vertex
                            setSelectedCoordinates({
                                x: vertexPosition.x.toFixed(2),
                                y: vertexPosition.y.toFixed(2),
                                z: vertexPosition.z.toFixed(2)
                            });
                            setSelectedVertex(index);

                            setSelectedValue(null);
                            setSelectedColor(null);

                            // Open the coordinate panel when a point is picked
                            setIsCoordinatePanelCollapsed(false);

                            // Look up brain region for the selected vertex
                            updateRegionFromVertex(index);

                            // Check for intensity data (color mapping) at the vertex
                            if (hasIntensityData && viewer.model_data) {
                                const modelData = viewer.model_data;
                                let intensityDataObj = null;

                                // Try to get the default intensity data if available
                                if (typeof modelData.getDefaultIntensityData === 'function') {
                                    intensityDataObj = modelData.getDefaultIntensityData();
                                } else if (modelData.intensity_data && modelData.intensity_data.length > 0) {
                                    intensityDataObj = modelData.intensity_data[0];
                                }

                                if (intensityDataObj && intensityDataObj.values && index !== undefined) {
                                    const intensityValue = intensityDataObj.values[index];

                                    if (typeof intensityValue !== 'undefined') {
                                        setSelectedValue(intensityValue.toFixed(2));

                                        if (viewer.color_map && typeof viewer.color_map.colorFromValue === 'function') {
                                            try {
                                                const hexColor = viewer.color_map.colorFromValue(intensityValue, {
                                                    hex: true,
                                                    min: intensityDataObj.range_min,
                                                    max: intensityDataObj.range_max
                                                });
                                                setSelectedColor(hexColor);
                                            } catch (colorError) {
                                                console.warn("Error getting color from value:", colorError);
                                                setSelectedColor(null);
                                            }
                                        } else {
                                            setSelectedColor(null);
                                        }
                                    } else {
                                        setSelectedValue(null);
                                        setSelectedColor(null);
                                    }
                                } else {
                                    setSelectedValue(null);
                                    setSelectedColor(null);
                                }
                            }

                            viewer.render();
                        } else {
                            console.log("Shift+Click: Pick did not intersect model.");
                        }
                    } catch (pickError) {
                        console.error("Error during picking or processing:", pickError);
                        setSelectedCoordinates(null);
                        setSelectedVertex(null);
                        setSelectedValue(null);
                        setSelectedColor(null);
                        setSelectedRegion(null);
                    }
                } else {
                    console.error("Shift+Click failed: viewer.pick is not a function.");
                }
            };

            // Add event listener to container
            const container = containerRef.current;
            if (container) container.addEventListener('click', handleClick);
            return () => { if (container) container.removeEventListener('click', handleClick); };
        }
    }, [viewer, hasIntensityData, setSelectedCoordinates, setSelectedVertex, setSelectedValue, setSelectedColor, updateRegionFromVertex, containerRef, markerRef, setIsCoordinatePanelCollapsed]);

    const applyOpacityToScene = useCallback((newOpacity) => {
        if (!viewer || typeof newOpacity !== 'number') {
            return;
        }

        if (viewer.model) {
            viewer.model.traverse(object => {
                const isBrainMesh =
                    object &&
                    object.isMesh &&
                    object.geometry &&
                    object.geometry.type !== 'SphereGeometry' &&
                    !object.name?.includes('-points') &&
                    object.name !== 'uploaded-points';

                const isWireframeHelper =
                    object &&
                    object.name === '__WIREFRAME__' &&
                    (object.isLine || object.type === 'Line' || object.type === 'LineSegments');

                if (!isBrainMesh && !isWireframeHelper) {
                    return;
                }

                const applyOpacityToMaterial = (material) => {
                    if (!material) {
                        return;
                    }

                    const needsWireframeTransparency =
                        wireframeEnabled &&
                        (Boolean(material.wireframe) || isWireframeHelper);

                    material.transparent = needsWireframeTransparency || newOpacity < 1.0;
                    material.opacity = newOpacity;
                };

                if (Array.isArray(object.material)) {
                    object.material.forEach(applyOpacityToMaterial);
                } else {
                    applyOpacityToMaterial(object.material);
                }
            });
        }

        if (viewer.setTransparency && typeof viewer.setTransparency === 'function' && viewer.model) {
            const brainModels = viewer.model.children.filter(shape =>
                shape.type === 'Mesh' &&
                shape.geometry &&
                shape.geometry.type !== 'SphereGeometry' &&
                !shape.name?.includes('-points') &&
                shape.name !== 'uploaded-points'
            );

            brainModels.forEach(model => {
                if (model.name) {
                    viewer.setTransparency(newOpacity, {
                        shape_name: model.name
                    });
                }
            });
        }
    }, [viewer, wireframeEnabled]);

    const applyWireframeState = useCallback((newWireframeState) => {
        if (!viewer) {
            return;
        }

        if (viewer.model) {
            viewer.model.traverse(object => {
                const isBrainMesh =
                    object &&
                    object.isMesh &&
                    object.geometry &&
                    object.geometry.type !== 'SphereGeometry' &&
                    !object.name?.includes('-points') &&
                    object.name !== 'uploaded-points';

                const isWireframeHelper =
                    object &&
                    object.name === '__WIREFRAME__' &&
                    (object.isLine || object.type === 'Line' || object.type === 'LineSegments');

                if (isBrainMesh && object.material) {
                    const updateMeshMaterial = (material) => {
                        if (!material) {
                            return;
                        }

                        material.wireframe = newWireframeState;
                        material.transparent = newWireframeState ? true : brainOpacity < 1.0;
                    };

                    if (Array.isArray(object.material)) {
                        object.material.forEach(updateMeshMaterial);
                    } else {
                        updateMeshMaterial(object.material);
                    }
                }

                if (isWireframeHelper && object.material) {
                    const updateWireframeMaterial = (material) => {
                        if (!material) {
                            return;
                        }

                        material.opacity = brainOpacity;
                        material.transparent = brainOpacity < 1.0 || newWireframeState;
                    };

                    if (Array.isArray(object.material)) {
                        object.material.forEach(updateWireframeMaterial);
                    } else {
                        updateWireframeMaterial(object.material);
                    }
                }
            });
        }

        if (viewer.setWireframe && typeof viewer.setWireframe === 'function') {
            viewer.setWireframe(newWireframeState);
        }
    }, [viewer, brainOpacity]);

    // Apply restored wireframe + opacity once a brain model is present, so persisted
    // values take visual effect on refresh (the initial-load path applies neither
    // through the normal handlers). Wireframe first, then opacity (which also runs
    // viewer.setTransparency for correct transparency). The wireframe overlay
    // (__WIREFRAME__) is built by an async Web Worker and defaults to full opacity,
    // so re-apply opacity a couple of times to catch it once it exists — otherwise
    // the wireframe stays fully opaque regardless of the opacity setting.
    useEffect(() => {
        if (!viewer || !currentObjectFilename) {
            return undefined;
        }
        applyWireframeState(wireframeEnabled);
        applyOpacityToScene(brainOpacity);
        const timers = wireframeEnabled
            ? [200, 600].map(delay => setTimeout(() => applyOpacityToScene(brainOpacity), delay))
            : [];
        return () => timers.forEach(clearTimeout);
    }, [viewer, currentObjectFilename, brainOpacity, wireframeEnabled, applyOpacityToScene, applyWireframeState]);

    // "Reset settings" signal: reload the current hemisphere surface WITHOUT
    // intensity so any applied colormap/atlas coloring is removed. Plotted points
    // are preserved. Skips the initial mount; only reacts to an actual reset.
    const resetHandledRef = useRef(false);
    useEffect(() => {
        if (!resetHandledRef.current) {
            resetHandledRef.current = true;
            return;
        }
        if (!viewer) {
            return;
        }

        const hemisphere = settingsState?.selectedHemisphere || 'lh_rh';
        const modelFiles = {
            lh: '/atlas/lh.pial.asc',
            rh: '/atlas/rh.pial.asc',
            lh_rh: '/atlas/lh_rh.pial.asc'
        };
        const modelFile = modelFiles[hemisphere] || '/atlas/lh_rh.pial.asc';

        // Preserve plotted points across the reload.
        const preserved = [];
        if (viewer.model) {
            viewer.model.traverse(object => {
                if (object?.name && (isPatientOrClinicalPointName(object.name) || object.name === 'uploaded-points')) {
                    preserved.push({ object, visible: object.visible });
                }
            });
            preserved.forEach(p => viewer.model.remove(p.object));
            while (viewer.model.children.length > 0) {
                viewer.model.remove(viewer.model.children[0]);
            }
        }
        viewer.model_data?.clear?.();

        viewer.loadModelFromURL(modelFile, {
            format: 'freesurferasc',
            complete: () => {
                preserved.forEach(p => {
                    viewer.model.add(p.object);
                    p.object.visible = p.visible;
                });
                applyOpacityToScene(brainOpacity);
                applyWireframeState(wireframeEnabled);
                setHasIntensityData(false);
                setCurrentObjectFilename(hemisphere === 'lh_rh' ? 'lh_rh.pial.asc' : `${hemisphere}.pial.asc`);
                viewer.render?.();
            },
            error: (error) => {
                console.error('Error reloading brain model on reset:', error);
            }
        });
        // Only react to the reset signal, not incidental dependency changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resetNonce]);

    const handleOpacityChange = (event) => {
        if (!viewer) {
            return;
        }

        const newOpacity = parseFloat(event.target.value) / 100;
        setBrainOpacity(newOpacity);
        applyOpacityToScene(newOpacity);
        viewer.render();
    };

    const handleToggleWireframe = () => {
        if (!viewer) {
            return;
        }

        const newWireframeState = !wireframeEnabled;
        setWireframeEnabled(newWireframeState);
        applyWireframeState(newWireframeState);
        viewer.render();
    };

    const handleToggleRegionLabels = () => {
        const nextValue = !showRegionLabels;
        setShowRegionLabels(nextValue);
        window.requestAnimationFrame(() => renderRegionLabelSprites(nextValue));
    };

    return (
        <div className="visualizer-card">
            <div className="visualizer-container" ref={containerRef} data-loading={patientsDataLoading}>
                <div id="brainbrowser" style={{ pointerEvents: patientsDataLoading ? 'none' : 'auto' }}></div>
                {showRegionLabels && regionLabelPositions.length > 0 && (
                    <div className="atlas-region-label-layer" aria-hidden="true">
                        {regionLabelPositions.map(region => (
                            <div
                                key={region.id}
                                className="atlas-region-label"
                                style={{
                                    left: `${region.x}px`,
                                    top: `${region.y}px`
                                }}
                                title={region.name}
                            >
                                {region.name}
                            </div>
                        ))}
                    </div>
                )}
                {patientsDataLoading && (
                    <div className="viewer-loading-indicator">
                        <div className="spinner"></div>
                        <span>Loading...</span>
                    </div>
                )}
                {/* Settings Button */}
                {!isSettingsOpen && (
                    <button
                        className="settings-button"
                        onClick={() => setIsSettingsOpen(!isSettingsOpen)}
                        title="Settings"
                    >
                        <Settings size={20} />
                    </button>
                )}
                {isViewControlsCollapsed ? (
                    <button
                        className="view-controls-button"
                        onClick={() => setIsViewControlsCollapsed(false)}
                        title="Show viewer controls"
                    >
                        <SlidersHorizontal size={20} />
                    </button>
                ) : (
                    <div className="viewer-controls">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', borderBottom: '1px solid rgba(255, 255, 255, 0.2)', paddingBottom: '8px' }}>
                            <span className="viewer-controls-title">Viewer Controls</span>
                            <button
                                className="viewer-controls-close-button"
                                onClick={() => setIsViewControlsCollapsed(true)}
                                style={{
                                    background: 'transparent',
                                    border: 'none',
                                    color: 'white',
                                    cursor: 'pointer',
                                    padding: '4px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                                title="Collapse viewer controls"
                            >
                                <X size={16} />
                            </button>
                        </div>
                        <div className="viewer-controls-content">
                            <div className="wireframe-control">
                                <Grid size={16} />
                                <span>Wireframe</span>
                                <div className="toggle-switch">
                                    <input
                                        type="checkbox"
                                        id="wireframe-toggle"
                                        className="toggle-checkbox"
                                        checked={wireframeEnabled}
                                        onChange={handleToggleWireframe}
                                    />
                                    <label htmlFor="wireframe-toggle" className="toggle-label"></label>
                                </div>
                            </div>

                            <div className="opacity-control">
                                <Sliders size={16} />
                                <span>Opacity</span>
                                <input
                                    type="range"
                                    min="0"
                                    max="100"
                                    value={brainOpacity * 100}
                                    onChange={handleOpacityChange}
                                    className="opacity-slider"
                                    title="Brain Opacity"
                                />
                            </div>
                            <button onClick={handleResetView}>
                                <RotateCcw size={16} />
                                <span>Reset View</span>
                            </button>
                            {onResetSettings && (
                                <button
                                    onClick={onResetSettings}
                                    title="Restore all viewer and z-score settings to their defaults"
                                >
                                    <RotateCcw size={16} />
                                    <span>Reset Settings</span>
                                </button>
                            )}
                            <button
                                onClick={handleToggleRegionLabels}
                                disabled={!atlasDataRef.current || !atlasLabelsRef.current}
                            >
                                {showRegionLabels ? <EyeOff size={16} /> : <Eye size={16} />}
                                <span>{showRegionLabels ? 'Hide Labels' : 'Show Labels'}</span>
                            </button>
                            <button
                                onClick={handleTogglePoints}
                                disabled={!hasPoints}
                                data-points-visible={arePointsVisible.toString()}
                            >
                                {arePointsVisible ? <EyeOff size={16} /> : <Eye size={16} />}
                                <span>{arePointsVisible ? 'Hide Points' : 'Show Points'}</span>
                            </button>
                            <button onClick={handleToggleBrain}>
                                {isBrainVisible ? <EyeOff size={16} /> : <Eye size={16} />}
                                <span>{isBrainVisible ? 'Hide Brain' : 'Show Brain'}</span>
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Coordinate Display Panel */}
            {isCoordinatePanelCollapsed ? (
                <button
                    className="coordinate-panel-button"
                    onClick={() => setIsCoordinatePanelCollapsed(false)}
                    title="Show coordinates"
                >
                    <MousePointerClick size={20} />
                </button>
            ) : (
                <div className="coordinate-panel">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', borderBottom: '1px solid rgba(255, 255, 255, 0.2)', paddingBottom: '8px' }}>
                        <h3 style={{ margin: 0 }}>{selectedCoordinates ? 'Selected Point' : 'Select a Point (Shift+Click)'}</h3>
                        <button
                            onClick={() => setIsCoordinatePanelCollapsed(true)}
                            style={{
                                background: 'transparent',
                                border: 'none',
                                color: 'white',
                                cursor: 'pointer',
                                padding: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                            title="Collapse coordinates"
                        >
                            <X size={16} />
                        </button>
                    </div>
                    {selectedCoordinates ? (
                        <div className="coordinate-info">
                            <span className="coordinate-label" style={{ fontSize: '14px' }}>Coordinates:</span>
                            <div className="coordinate-xyz-container">
                                <div className="coordinate-row">
                                    <span className="coordinate-label">x:</span>
                                    <span className="coordinate-value">{selectedCoordinates.x}</span>
                                </div>
                                <div className="coordinate-row">
                                    <span className="coordinate-label">y:</span>
                                    <span className="coordinate-value">{selectedCoordinates.y}</span>
                                </div>
                                <div className="coordinate-row">
                                    <span className="coordinate-label">z:</span>
                                    <span className="coordinate-value">{selectedCoordinates.z}</span>
                                </div>
                            </div>
                            {selectedVertex !== null && (
                                <div className="coordinate-row">
                                    <span className="coordinate-label">Vertex:</span>
                                    <span className="coordinate-value">{selectedVertex}</span>
                                </div>
                            )}
                            {selectedRegion !== null && (
                                <div className="coordinate-row region-row">
                                    <span className="coordinate-label">Region:</span>
                                    <span className="coordinate-value region-value" title={selectedRegion}>{selectedRegion}</span>
                                </div>
                            )}
                            {selectedValue !== null && (
                                <div className="coordinate-row">
                                    <span className="coordinate-label">Value:</span>
                                    <span className="coordinate-value">
                                        {selectedColor && <span className="coordinate-color" style={{ backgroundColor: `#${selectedColor}`, marginRight: '6px' }}></span>}
                                        {selectedValue}
                                    </span>
                                </div>
                            )}
                            <button
                                className="clear-selection-button"
                                onClick={handleClearSelection}
                                title="Clear selection"
                            >
                                <X size={14} />
                                <span>Clear Selection</span>
                            </button>
                        </div>
                    ) : (
                        <p className="no-selection">Hold the Shift key ⇧ and click to select a point on the brain.</p>
                    )}
                </div>
            )}

            {/* Clinical metadata legend (floats over the 3D view) */}
            {showClinicalLegend && (
                isLegendCollapsed ? (
                    <button
                        className="clinical-legend-button"
                        onClick={() => setIsLegendCollapsed(false)}
                        title="Show clinical metadata legend"
                    >
                        <ChartScatter size={18} />
                    </button>
                ) : (
                    <div className="clinical-legend-overlay">
                        <div className="clinical-legend-header">
                            <h3>Clinical metadata</h3>
                            <button
                                onClick={() => setIsLegendCollapsed(true)}
                                title="Hide legend"
                                style={{
                                    background: 'transparent',
                                    border: 'none',
                                    color: 'white',
                                    cursor: 'pointer',
                                    padding: '4px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                <X size={16} />
                            </button>
                        </div>
                        <div className="clinical-legend-items">
                            {CLINICAL_OVERLAY_LEGEND.map(item => (
                                <span key={item.label}>
                                    <i style={{ backgroundColor: item.color, '--dot': item.color }} />
                                    {item.label}
                                </span>
                            ))}
                        </div>
                    </div>
                )
            )}

            {/* Settings Panel */}
            {isSettingsOpen && (
                <SettingsPanel
                    viewer={viewer}
                    selectedFormat={selectedFormat}
                    setSelectedFormat={setSelectedFormat}
                    selectedBackground={selectedBackground}
                    setSelectedBackground={setSelectedBackground}
                    hasIntensityData={hasIntensityData}
                    setHasIntensityData={setHasIntensityData}
                    currentObjectFilename={currentObjectFilename}
                    setCurrentObjectFilename={setCurrentObjectFilename}
                    onClose={() => setIsSettingsOpen(false)}
                    settingsState={settingsState}
                    setSettingsState={setSettingsState}
                    brainOpacity={brainOpacity}
                    wireframeEnabled={wireframeEnabled}
                    applyOpacityToScene={applyOpacityToScene}
                    applyWireframeState={applyWireframeState}
                />
            )}
        </div>
    );
});
